import { roundToTwoDecimals } from '@/lib/utils';

export const UNIT_VALUE = 20;

export type NormalizedPlayerRow = {
  name: string;
  buyinCount: number;
  totalBuyinAmount: number;
  cashoutAmount: number;
  net: number;
  variance: number;
  note: string | null;
};

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Normalize an extracted "player row" from any source (LLM, OCR JSON, manual edits)
 * into the app's canonical shape, while computing derived values deterministically.
 */
export function normalizePlayerRow(
  raw: unknown,
  unitValue = UNIT_VALUE
): NormalizedPlayerRow {
  const obj = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};

  const nameRaw = obj.name ?? obj.player ?? obj.Player ?? '';
  const name = String(nameRaw ?? '').trim();

  const buyinCountRaw =
    toFiniteNumber(obj.buyinCount ?? obj.buyins ?? obj.buy_ins ?? obj.Buyins ?? obj.buyInCount ?? obj.entries ?? obj.entryCount) ?? 0;
  const buyinCount = Math.max(0, Math.round(buyinCountRaw));

  const cashoutAmount =
    toFiniteNumber(obj.cashoutAmount ?? obj.cashout ?? obj.cashOut ?? obj.cash_out ?? obj.Cashout ?? obj.results ?? obj.result ?? obj.Results) ?? 0;

  const explicitNet =
    toFiniteNumber(obj.net ?? obj.score ?? obj.Score ?? obj.netAmount ?? obj.net_amount ?? obj.Balance ?? obj.balance ?? obj.Profit ?? obj.profit ?? obj.Loss ?? obj.loss);

  const noteRaw = obj.note ?? obj.Note ?? obj.comment ?? obj.Comment ?? '';
  const note = String(noteRaw ?? '').trim() || null;

  // 3. strictly compute totalBuyinAmount = buyinCount * unitValue
  const totalBuyinAmount = roundToTwoDecimals(buyinCount * unitValue);

  // 4. net calculation
  const computedNet = roundToTwoDecimals(roundToTwoDecimals(cashoutAmount) - totalBuyinAmount);
  const net = explicitNet !== null ? explicitNet : computedNet;

  // 5. The variance tracks explicitNet - computedNet
  const variance = explicitNet !== null ? roundToTwoDecimals(explicitNet - computedNet) : 0;

  return {
    name,
    buyinCount,
    totalBuyinAmount,
    cashoutAmount: roundToTwoDecimals(cashoutAmount),
    net,
    variance,
    note,
  };
}

/** Sum of positive nets, sum of negative nets, total buy-ins, and their variance (Σ net — should be 0 when the sheet balances). */
export function aggregateSessionStats(players: Array<{ net: number; totalBuyinAmount?: number }>): {
  totalPositive: number;
  totalNegative: number;
  totalPot: number;
  variance: number;
} {
  const totalPositive = players.reduce((s, p) => s + (p.net > 0 ? p.net : 0), 0);
  const totalNegative = players.reduce((s, p) => s + (p.net < 0 ? p.net : 0), 0);
  const totalPot = players.reduce((s, p) => s + (p.totalBuyinAmount ?? 0), 0);
  return { 
    totalPositive: roundToTwoDecimals(totalPositive), 
    totalNegative: roundToTwoDecimals(totalNegative), 
    totalPot: roundToTwoDecimals(totalPot),
    variance: roundToTwoDecimals(totalPositive + totalNegative) 
  };
}

/** @deprecated Use aggregateSessionStats instead */
export const aggregatePlayerNets = aggregateSessionStats;
